import { findPasswordResetRequirement, LoginResponse, requirementsFrom, SecondFactorKind, SECOND_FACTOR_URN } from '@/types'
import { useResendTwoFactor, useVerifyTwoFactor, useVerifyWebAuthn } from '@/services'
import { isWebAuthnSupported } from '@/utils/webauthn'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OTPInput from 'react-otp-input'

const serverError = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback

/**
 * The login-time second-factor step, rendered from the methods the server advertised for the
 * account (read out of the 428 `requirements`). Strong-only accounts get TOTP and/or a passkey;
 * un-enrolled accounts get the emailed code. The `code`-based factors share one 6-digit box but send
 * a different `method`: TOTP sends the strong kind, the legacy emailed/SMS code sends none. WebAuthn
 * runs the browser ceremony instead of a code. On success the parent finishes the sign-in.
 */
export const TwoFactorChallenge = ({
  challengeToken,
  methods,
  email,
  onVerified,
  onBack,
  onPasswordResetRequired,
}: {
  challengeToken: string
  methods: SecondFactorKind[]
  email?: string
  onVerified: (response: LoginResponse) => void
  onBack?: () => void
  /**
   * The server halted AFTER the factor verified because the account must reset its password, and
   * handed over a token to do it in place. The parent swaps to the reset form; nothing is shown here.
   */
  onPasswordResetRequired?: (resetToken: string) => void
}) => {
  const { t } = useTranslation()
  const verify = useVerifyTwoFactor()
  const verifyWebAuthn = useVerifyWebAuthn()
  const resend = useResendTwoFactor()

  const has = (urn: string) => methods.some((m) => m.type === urn)
  const passkeyOffered = has(SECOND_FACTOR_URN.WEBAUTHN) && isWebAuthnSupported()
  // The single code-based factor to prompt for: prefer the authenticator, else the emailed/SMS code.
  const codeKind = has(SECOND_FACTOR_URN.TOTP)
    ? SECOND_FACTOR_URN.TOTP
    : has(SECOND_FACTOR_URN.EMAIL)
      ? SECOND_FACTOR_URN.EMAIL
      : has(SECOND_FACTOR_URN.SMS)
        ? SECOND_FACTOR_URN.SMS
        : undefined
  const isTotp = codeKind === SECOND_FACTOR_URN.TOTP
  const isEmailOrSms = codeKind === SECOND_FACTOR_URN.EMAIL || codeKind === SECOND_FACTOR_URN.SMS

  // Start on a passkey when it's the only thing offered; otherwise show the code box first.
  const [usePasskey, setUsePasskey] = useState(passkeyOffered && !codeKind)
  const [useRecovery, setUseRecovery] = useState(false)
  const [code, setCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [error, setError] = useState<string | undefined>()

  // A verify rejection is either a bad factor (shown inline) or the post-factor forced-reset halt,
  // which carries a reset token — that one is a step forward, not an error.
  const failOrReset = (e: any, fallback: string) => {
    const resetToken = findPasswordResetRequirement(requirementsFrom(e))?.resetToken
    if (resetToken && onPasswordResetRequired) {
      setError(undefined)
      onPasswordResetRequired(resetToken)
      return
    }
    setError(serverError(e, fallback))
  }

  // A recovery code is the backup for any strong-only account — offered even when the browser can't
  // run the passkey ceremony (someone who lost their authenticator and can't use their passkey here).
  const recoveryAvailable = has(SECOND_FACTOR_URN.TOTP) || has(SECOND_FACTOR_URN.WEBAUTHN)

  const title = useMemo(() => {
    if (useRecovery) return t('twoFactor.recoveryTitle', 'Enter a recovery code')
    if (usePasskey) return t('twoFactor.usePasskeyTitle', 'Use your passkey')
    if (isTotp) return t('twoFactor.authTitle', 'Enter your authenticator code')
    return t('twoFactor.emailTitle', 'Check your email')
  }, [useRecovery, usePasskey, isTotp, t])

  const submitCode = () => {
    if (code.length !== 6) return
    setError(undefined)
    verify.mutate(
      {
        challengeToken,
        code,
        // Strong factor -> send the kind; legacy delivered code -> omit it.
        method: isTotp ? { type: SECOND_FACTOR_URN.TOTP } : undefined,
      },
      {
        onSuccess: (data) => onVerified(data),
        onError: (e) => failOrReset(e, t('twoFactor.invalidCode', 'That code was not accepted. Try again.')),
      }
    )
  }

  const submitPasskey = () => {
    setError(undefined)
    verifyWebAuthn.mutate(
      { challengeToken },
      {
        onSuccess: (data) => onVerified(data),
        onError: (e) => failOrReset(e, t('twoFactor.passkeyFailed', 'Passkey sign-in failed. Try again.')),
      }
    )
  }

  const submitRecovery = () => {
    const value = recoveryCode.trim()
    if (value.length < 6) return
    setError(undefined)
    verify.mutate(
      { challengeToken, code: value, method: { type: SECOND_FACTOR_URN.RECOVERY } },
      {
        onSuccess: (data) => onVerified(data),
        onError: (e) =>
          failOrReset(e, t('twoFactor.invalidRecovery', 'That recovery code was not accepted.')),
      }
    )
  }

  const onResend = () =>
    resend.mutate(
      { challengeToken },
      {
        onSuccess: () => setError(undefined),
        onError: (e) => failOrReset(e, t('twoFactor.resendFailed', 'Could not resend the code.')),
      }
    )

  return (
    <div className="w-[300px] xs:w-[345px] flex flex-col gap-y-8 text-center">
      <p className="text-xl font-bold text-white">{title}</p>

      {useRecovery ? (
        <>
          <p className="text-white text-sm leading-relaxed">
            {t('twoFactor.recoverySubtitle', 'Enter one of your one-time recovery codes.')}
          </p>
          <input
            type="text"
            autoFocus
            value={recoveryCode}
            onChange={(e) => {
              setRecoveryCode(e.target.value)
              setError(undefined)
            }}
            onKeyDown={(e) => e.key === 'Enter' && submitRecovery()}
            placeholder="XXXXX-XXXXX"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            className="w-full rounded-2xl p-3 text-center font-mono tracking-widest uppercase text-navyBlue focus:outline-none"
          />
          <button
            type="button"
            disabled={verify.isLoading || recoveryCode.trim().length < 6}
            onClick={submitRecovery}
            className={`${verify.isLoading || recoveryCode.trim().length < 6 ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white hover:bg-primary'} w-full rounded-full py-3 px-5`}
          >
            {verify.isLoading ? t('common.loading', 'Loading…') : t('twoFactor.verify', 'Verify')}
          </button>
        </>
      ) : usePasskey ? (
        <>
          <p className="text-white text-sm leading-relaxed">
            {t('twoFactor.usePasskeySubtitle', 'Use your device fingerprint, face, or screen lock to sign in.')}
          </p>
          <button
            type="button"
            disabled={verifyWebAuthn.isLoading}
            onClick={submitPasskey}
            className={`${verifyWebAuthn.isLoading ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white hover:bg-primary'} w-full rounded-full py-3 px-5`}
          >
            {verifyWebAuthn.isLoading ? t('common.loading', 'Loading…') : t('twoFactor.usePasskey', 'Use a passkey')}
          </button>
        </>
      ) : (
        <>
          <p className="text-white text-sm leading-relaxed">
            {isTotp
              ? t('twoFactor.authSubtitle', 'Enter the 6-digit code from your authenticator app.')
              : t('twoFactor.emailSubtitle', 'Enter the 6-digit code we sent to {{email}}.', { email: email ?? '' })}
          </p>
          <OTPInput
            value={code}
            onChange={(v) => {
              setCode(v.replace(/\D/g, '').slice(0, 6))
              setError(undefined)
            }}
            numInputs={6}
            inputStyle="flex-1 border rounded-2xl aspect-square max-w-[56px] text-center text-2xl text-navyBlue"
            containerStyle="justify-between gap-x-2"
            renderInput={(props) => <input {...props} inputMode="numeric" autoComplete="one-time-code" />}
          />
          <button
            type="button"
            disabled={verify.isLoading || code.length !== 6}
            onClick={submitCode}
            className={`${verify.isLoading || code.length !== 6 ? 'bg-gray-400 text-gray-200' : 'bg-azul text-white hover:bg-primary'} w-full rounded-full py-3 px-5`}
          >
            {verify.isLoading ? t('common.loading', 'Loading…') : t('twoFactor.verify', 'Verify')}
          </button>
          {/* A resend only makes sense for a delivered code; the server refuses it once a strong factor is enrolled. */}
          {isEmailOrSms && (
            <button
              type="button"
              disabled={resend.isLoading}
              onClick={onResend}
              className="text-white text-center hover:text-opacity-70 disabled:opacity-50"
            >
              {t('twoFactor.resend', 'Send a new code')}
            </button>
          )}
        </>
      )}

      {error && <p className="text-xs text-warn">{error}</p>}

      <div className="flex flex-col items-center gap-y-2">
        {/* Let the user switch between the offered strong factors. */}
        {!useRecovery && passkeyOffered && codeKind && (
          <button type="button" onClick={() => setUsePasskey((v) => !v)} className="text-white/70 text-xs hover:text-white">
            {usePasskey
              ? t('twoFactor.useCodeInstead', 'Use a code instead')
              : t('twoFactor.usePasskeyInstead', 'Use a passkey instead')}
          </button>
        )}
        {/* Recovery codes are the lost-device backup for a strong-only account. */}
        {recoveryAvailable && (
          <button
            type="button"
            onClick={() => {
              setUseRecovery((v) => !v)
              setError(undefined)
            }}
            className="text-white/70 text-xs hover:text-white"
          >
            {useRecovery
              ? t('twoFactor.useFactorInstead', 'Use your authenticator or passkey')
              : t('twoFactor.useRecovery', 'Use a recovery code')}
          </button>
        )}
        {onBack && (
          <button type="button" onClick={onBack} className="text-white/60 text-xs hover:text-white">
            {t('twoFactor.back', 'Back to sign in')}
          </button>
        )}
      </div>
    </div>
  )
}
